/**
 * Card inline de postagem do Instagram no corpo do chat
 * Exibe a postagem como uma mensagem separada no fluxo de conversação
 * Permite vincular comentários através do sistema de quote
 * 
 * @version 1.0.0
 * @date 2026-01-18
 */

import { useState } from 'react';
import { ExternalLink, Image, Film, Images, Instagram } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Interface de props do componente
export interface PostCardBubbleProps {
  /** ID único da postagem (media_id) para referência */
  postId: string;
  /** URL da thumbnail da postagem */
  mediaUrl?: string | null;
  /** Tipo de mídia (IMAGE, VIDEO, REELS, CAROUSEL_ALBUM) */
  mediaType?: string;
  /** Link direto para postagem no Instagram (permalink) */
  mediaPermalink?: string | null;
  /** Data/hora do primeiro comentário nesta postagem */
  timestamp: string;
  /** Quantidade de comentários nesta postagem */
  commentCount?: number;
  /** Classes CSS adicionais */
  className?: string;
  /** Ref para scroll */
  innerRef?: React.Ref<HTMLDivElement>;
}

/**
 * Retorna ícone apropriado para o tipo de mídia
 */
function getMediaIcon(mediaType?: string) {
  switch (mediaType?.toUpperCase()) {
    case 'VIDEO':
    case 'REELS':
      return <Film className="h-8 w-8 text-muted-foreground/60" />;
    case 'CAROUSEL_ALBUM':
      return <Images className="h-8 w-8 text-muted-foreground/60" />;
    default:
      return <Image className="h-8 w-8 text-muted-foreground/60" />;
  }
}

/**
 * Formata o tipo de mídia para exibição
 */
function formatMediaType(mediaType?: string): string {
  switch (mediaType?.toUpperCase()) {
    case 'VIDEO':
      return 'Vídeo';
    case 'REELS':
      return 'Reels';
    case 'CAROUSEL_ALBUM':
      return 'Carrossel';
    default:
      return 'Imagem';
  }
}

/**
 * Componente que exibe a postagem do Instagram como card inline no chat
 * Aparece no fluxo de mensagens na posição cronológica do primeiro comentário
 */
export function PostCardBubble({
  postId,
  mediaUrl,
  mediaType,
  mediaPermalink,
  timestamp,
  commentCount = 0,
  className,
  innerRef,
}: PostCardBubbleProps) {
  const [imageError, setImageError] = useState(false);
  
  // Verifica se tem link válido para o Instagram
  const hasValidLink = !!mediaPermalink;
  
  // Handler para abrir postagem no Instagram
  const handleOpenInInstagram = () => {
    if (mediaPermalink) {
      window.open(mediaPermalink, '_blank', 'noopener,noreferrer');
    }
  };

  // Formata a data/hora
  const formattedTime = format(new Date(timestamp), 'HH:mm', { locale: ptBR });

  return (
    <div
      ref={innerRef}
      data-post-id={postId}
      className={cn(
        'flex justify-center my-3',
        className
      )}
    >
      {/* Card da postagem com visual diferenciado */}
      <div className={cn(
        'relative flex items-center gap-3 p-3 rounded-xl border',
        'bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20',
        'border-amber-200/50 dark:border-amber-800/30',
        'max-w-[90%] md:max-w-[60%]'
      )}>
        {/* Badge Instagram no canto superior */}
        <div className="absolute -top-2 left-3 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white flex items-center gap-1">
          <Instagram className="h-2.5 w-2.5" />
          Postagem
        </div>

        {/* Thumbnail da postagem */}
        <div className="relative h-16 w-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 mt-1">
          {mediaUrl && !imageError ? (
            <img
              src={mediaUrl}
              alt="Postagem do Instagram"
              className="h-full w-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30">
              {getMediaIcon(mediaType)}
            </div>
          )}
          
          {/* Badge de tipo no canto inferior */}
          <div className="absolute bottom-0.5 right-0.5 bg-black/60 rounded px-1 py-0.5">
            <span className="text-[8px] font-medium text-white">
              {formatMediaType(mediaType)}
            </span>
          </div>
        </div>

        {/* Informações da postagem */}
        <div className="flex-1 min-w-0 mt-1">
          <p className="text-sm font-medium text-foreground">
            Postagem do Instagram
          </p>
          
          {/* Contador de comentários */}
          {commentCount > 0 && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {commentCount} {commentCount === 1 ? 'comentário' : 'comentários'} recebidos
            </p>
          )}
          
          {/* Hora e link */}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-[10px] text-muted-foreground">
              {formattedTime}
            </span>
            
            {hasValidLink ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[10px] gap-1 text-amber-700 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300"
                onClick={handleOpenInInstagram}
              >
                <ExternalLink className="h-3 w-3" />
                Ver no Instagram
              </Button>
            ) : (
              <span className="text-[10px] text-muted-foreground/60 italic">
                Link indisponível
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
